import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  approveNewInvestment,
  deleteInvestment,
  returnNewInvestment,
} from "../../actions/newInvestments";

const safeParse = (v) => {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const money = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const day = (v) => (v ? moment(v).format("DD-MM-YYYY") : "—");
const when = (v) => (v ? moment(v).format("DD-MM-YYYY HH:mm") : "");

const fullName = (results) =>
  `${results?.firstName || ""} ${results?.lastName || ""}`.trim();

// Same rule as the server: nobody reviews what they entered themselves.
// Older entries only carry the name of whoever typed them in.
const isOwnEntry = (inv, results) => {
  if (!results) return false;
  if (inv.submitted_by_id) return String(inv.submitted_by_id) === results._id;
  const enteredBy = String(inv.uploaded_by || "")
    .trim()
    .toLowerCase();
  return !!enteredBy && enteredBy === fullName(results).toLowerCase();
};

const columns = [
  { id: "code", label: "Code", minWidth: 170 },
  { id: "investment_type", label: "Investment type", minWidth: 150 },
  { id: "institution", label: "Institution", minWidth: 150 },
  { id: "cost", label: "Cost (GHS)", minWidth: 130, align: "right" },
  { id: "rate", label: "Rate (%)", minWidth: 80, align: "right" },
  { id: "purchase_date", label: "Purchase date", minWidth: 110 },
  { id: "maturity_date", label: "Maturity date", minWidth: 110 },
  { id: "uploaded_by", label: "Entered by", minWidth: 140 },
];

function cellValue(inv, id) {
  if (id === "cost") return money(inv.cost);
  if (id === "purchase_date" || id === "maturity_date") return day(inv[id]);
  return inv[id] ?? "—";
}

function Detail({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, opacity: 0.6 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
        {value === "" || value == null ? "—" : value}
      </Typography>
    </Box>
  );
}

const HISTORY_LABELS = {
  submitted: "Submitted",
  resubmitted: "Corrected and resubmitted",
  returned: "Returned",
  approved: "Approved",
};

export default function Approval() {
  const dispatch = useDispatch();
  const user = React.useMemo(
    () => safeParse(secureLocalStorage.getItem("profile")),
    [],
  );
  const canApprove = ["Administrator", "Approver"].includes(
    user?.results?.portfolio_manager,
  );

  const newInvestments = useSelector((state) => state.newInvestment || []);

  const [view, setView] = React.useState("pending");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [selected, setSelected] = React.useState(null);
  const [returning, setReturning] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteText, setDeleteText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const pending = newInvestments.filter(
    (inv) => inv.approval_status !== "returned",
  );
  const returned = newInvestments.filter(
    (inv) => inv.approval_status === "returned",
  );
  const rows = view === "pending" ? pending : returned;

  React.useEffect(() => {
    setPage(0);
  }, [view]);
  // Keep the page in range as items are approved off the list
  React.useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [rows.length, rowsPerPage, page]);

  if (!canApprove) return <Navigate to="/investment/" replace />;

  const ownEntry = selected ? isOwnEntry(selected, user?.results) : false;
  const isReturned = selected?.approval_status === "returned";

  const openReview = (inv) => {
    setSelected(inv);
    setReturning(false);
    setReason("");
  };
  const closeReview = () => {
    if (busy) return;
    setSelected(null);
    setReturning(false);
    setReason("");
  };

  const run = async (action) => {
    setBusy(true);
    try {
      await dispatch(action);
      setSelected(null);
      setReturning(false);
      setReason("");
      setDeleteOpen(false);
      setDeleteText("");
    } catch {
      // the action has already shown the error
    } finally {
      setBusy(false);
    }
  };

  const approve = () => run(approveNewInvestment(selected._id));
  const sendBack = () => run(returnNewInvestment(selected._id, reason.trim()));
  const remove = () => run(deleteInvestment(selected._id));

  const openDelete = (inv) => {
    setSelected(inv);
    setDeleteText("");
    setDeleteOpen(true);
  };
  const closeDelete = () => {
    if (busy) return;
    setDeleteOpen(false);
    setSelected(null);
    setDeleteText("");
  };
  const isDeleteAllowed = deleteText.trim().toUpperCase() === "DELETE";

  const fairValueAtMaturity =
    selected?.rate_type === "discount rate"
      ? Number(selected?.cost || 0)
      : Number(selected?.cost || 0) + Number(selected?.total_interest || 0);

  return (
    <Box sx={{ width: "100%" }}>
      <Helmet title="Approvals | Portfolio Manager" />

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                Approvals
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                Check each investment against its certificate before it goes
                live. You can&apos;t approve investments you entered yourself.
              </Typography>
            </Box>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
            >
              <ToggleButton value="pending" sx={{ textTransform: "none" }}>
                Awaiting approval ({pending.length})
              </ToggleButton>
              <ToggleButton value="returned" sx={{ textTransform: "none" }}>
                Returned ({returned.length})
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>

        <Divider />

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table
            size="small"
            stickyHeader
            aria-label="investments awaiting approval"
            sx={{
              "& thead th": {
                backgroundColor: "#f7f7fb",
                fontWeight: 900,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& tr > *:last-child": {
                position: "sticky",
                right: 0,
                backgroundColor: "#fff",
                zIndex: 2,
              },
              "& thead tr > *:last-child": { backgroundColor: "#f7f7fb" },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {view === "returned" && (
                  <TableCell sx={{ minWidth: 200 }}>Reason returned</TableCell>
                )}
                <TableCell align="center" sx={{ minWidth: 110 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 2}
                    align="center"
                    sx={{ py: 5, opacity: 0.7 }}
                  >
                    {view === "pending"
                      ? "Nothing is waiting for approval."
                      : "No investments are waiting for correction."}
                  </TableCell>
                </TableRow>
              )}

              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((inv) => (
                  <TableRow hover key={inv._id}>
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align}>
                        {cellValue(inv, column.id)}
                      </TableCell>
                    ))}
                    {view === "returned" && (
                      <TableCell sx={{ color: "warning.dark" }}>
                        {inv.returned_reason}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="Review">
                          <IconButton
                            size="small"
                            onClick={() => openReview(inv)}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => openDelete(inv)}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>

      {/* REVIEW */}
      <Dialog
        open={!!selected && !deleteOpen}
        onClose={closeReview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 900, display: "flex", alignItems: "center" }}
        >
          Review investment
          <IconButton
            onClick={closeReview}
            size="small"
            sx={{ ml: "auto" }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {selected && (
          <DialogContent dividers>
            {ownEntry && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You entered this investment, so another Administrator or
                Approver must review it.
              </Alert>
            )}
            {isReturned && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Returned by {selected.returned_by} on{" "}
                {when(selected.returned_at)}: {selected.returned_reason}. It
                can be approved once {selected.uploaded_by || "the person who entered it"}{" "}
                corrects it.
              </Alert>
            )}

            <Typography sx={{ fontWeight: 900, fontSize: 16, mb: 1.5 }}>
              {selected.code}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              <Detail label="Institution" value={selected.institution} />
              <Detail label="Investment type" value={selected.investment_type} />
              <Detail label="Regulator" value={selected.regulator} />
              <Detail
                label="Certificate no."
                value={selected.certificate_number}
              />
              <Detail label="Cost (GHS)" value={money(selected.cost)} />
              <Detail label="Rate (%)" value={selected.rate} />
              <Detail label="Rate type" value={selected.rate_type} />
              <Detail label="Charges (GHS)" value={money(selected.charges)} />
              <Detail label="Purchase date" value={day(selected.purchase_date)} />
              <Detail label="Tenure (days)" value={selected.tenure} />
              <Detail label="Year days" value={selected.year_days} />
              <Detail label="Maturity date" value={day(selected.maturity_date)} />
            </Box>

            <Paper
              variant="outlined"
              sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#f8fafc" }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 900, mb: 1 }}>
                Calculated by the system — check against the certificate
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <Detail
                  label="Total interest (GHS)"
                  value={money(selected.total_interest)}
                />
                <Detail
                  label="Daily interest (GHS)"
                  value={Number(selected.daily_interest || 0).toFixed(4)}
                />
                <Detail
                  label="Value at maturity (GHS)"
                  value={money(fairValueAtMaturity)}
                />
              </Box>
            </Paper>

            <Typography sx={{ fontSize: 12, fontWeight: 900, mt: 2, mb: 0.5 }}>
              History
            </Typography>
            {(selected.approval_history || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
                Entered by {selected.uploaded_by || "—"}
              </Typography>
            ) : (
              selected.approval_history.map((h, i) => (
                <Typography key={i} sx={{ fontSize: 13 }}>
                  {when(h.at)} — {HISTORY_LABELS[h.action] || h.action} by{" "}
                  {h.by}
                  {h.note ? `: ${h.note}` : ""}
                </Typography>
              ))
            )}

            {returning && (
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                size="small"
                sx={{ mt: 2 }}
                label="Why is it being returned?"
                placeholder="e.g. Rate on the certificate is 27.5%, not 25%"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </DialogContent>
        )}

        <DialogActions sx={{ p: 2 }}>
          {returning ? (
            <>
              <Button
                onClick={() => setReturning(false)}
                disabled={busy}
                sx={{ textTransform: "none" }}
              >
                Back
              </Button>
              <Button
                onClick={sendBack}
                variant="contained"
                color="warning"
                disabled={busy || !reason.trim()}
                startIcon={<UndoIcon />}
                sx={{ textTransform: "none" }}
              >
                {busy ? "Returning..." : "Return for correction"}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={closeReview}
                disabled={busy}
                sx={{ textTransform: "none", mr: "auto" }}
              >
                Close
              </Button>
              <Button
                onClick={() => setReturning(true)}
                variant="outlined"
                color="warning"
                disabled={busy || ownEntry || isReturned}
                startIcon={<UndoIcon />}
                sx={{ textTransform: "none" }}
              >
                Return
              </Button>
              <Button
                onClick={approve}
                variant="contained"
                disabled={busy || ownEntry || isReturned}
                startIcon={<CheckOutlinedIcon />}
                sx={{ textTransform: "none" }}
              >
                {busy ? "Approving..." : "Approve"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* DELETE (TYPE DELETE) */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ fontWeight: 900, display: "flex", alignItems: "center" }}
        >
          Delete Investment
          <IconButton
            onClick={closeDelete}
            size="small"
            sx={{ ml: "auto" }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <WarningAmberIcon sx={{ color: "error.main" }} />
            <Typography sx={{ fontWeight: 900 }}>
              This action is permanent.
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, mb: 2, opacity: 0.85 }}>
            To send it back for correction instead, use Review → Return. To
            delete, type <b>DELETE</b> below.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900, opacity: 0.7 }}>
              Code
            </Typography>
            <Typography sx={{ fontWeight: 900 }}>
              {selected?.code || "—"}
            </Typography>
          </Paper>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label='Type "DELETE" to confirm'
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            placeholder="DELETE"
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeDelete}
            variant="outlined"
            disabled={busy}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={remove}
            variant="contained"
            color="error"
            disabled={!isDeleteAllowed || busy}
            startIcon={<DeleteOutlineOutlinedIcon />}
            sx={{ textTransform: "none" }}
          >
            {busy ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
